import { CATEGORIES, RECOMMENDATIONS, WEIGHT_TOTAL } from "./rubric.js";
function round1(n) {
    return Math.round(n * 10) / 10;
}
function mean(values) {
    if (values.length === 0)
        return null;
    return round1(values.reduce((a, b) => a + b, 0) / values.length);
}
function spread(values) {
    if (values.length === 0)
        return null;
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((a, b) => a + (b - avg) ** 2, 0) / values.length;
    return {
        min: Math.min(...values),
        max: Math.max(...values),
        stdev: round1(Math.sqrt(variance)),
    };
}
/**
 * Derive each category's score as the mean (1 decimal) of the signal scores the
 * panelist actually gave in that category. Categories with no scored signals are
 * omitted. This is the single source of truth for category scores — the form
 * shows it read-only and the API stores it on save.
 */
export function deriveCategoryScores(signalScores) {
    const out = {};
    for (const cat of CATEGORIES) {
        const vals = cat.signals
            .map((s) => signalScores[cat.id]?.[s.id])
            .filter((v) => typeof v === "number");
        if (vals.length)
            out[cat.id] = round1(vals.reduce((a, b) => a + b, 0) / vals.length);
    }
    return out;
}
/**
 * One panelist's weighted overall score (out of 5), using only the categories
 * they actually scored. Returns null if no category scored yet.
 */
export function weightedOverall(scorecard) {
    let weightedSum = 0;
    let weightUsed = 0;
    for (const cat of CATEGORIES) {
        const score = scorecard.categoryScores[cat.id];
        if (typeof score === "number") {
            weightedSum += score * cat.weight;
            weightUsed += cat.weight;
        }
    }
    if (weightUsed === 0)
        return null;
    return round1(weightedSum / weightUsed);
}
const emptyTally = () => RECOMMENDATIONS.reduce((acc, r) => {
    acc[r.value] = 0;
    return acc;
}, {});
/**
 * Aggregate across all *submitted* scorecards for a candidate. Callers should
 * pass only submitted scorecards; this defensively filters again.
 */
export function computeAggregate(candidateId, scorecards) {
    const submitted = scorecards.filter((s) => s.submitted);
    const categoryAverages = {};
    const categorySpread = {};
    const signalAverages = {};
    const redFlagTally = {};
    for (const cat of CATEGORIES) {
        const catScores = submitted
            .map((s) => s.categoryScores[cat.id])
            .filter((v) => typeof v === "number");
        categoryAverages[cat.id] = mean(catScores);
        categorySpread[cat.id] = spread(catScores);
        signalAverages[cat.id] = {};
        for (const signal of cat.signals) {
            const vals = submitted
                .map((s) => s.signalScores[cat.id]?.[signal.id])
                .filter((v) => typeof v === "number");
            signalAverages[cat.id][signal.id] = mean(vals);
        }
        if (cat.redFlags?.length) {
            redFlagTally[cat.id] = {};
            for (const flag of cat.redFlags) {
                redFlagTally[cat.id][flag.id] = submitted.filter((s) => (s.redFlags[cat.id] ?? []).includes(flag.id)).length;
            }
        }
    }
    const overalls = submitted
        .map((s) => weightedOverall(s))
        .filter((v) => typeof v === "number");
    const recommendationTally = emptyTally();
    for (const s of submitted) {
        if (s.recommendation)
            recommendationTally[s.recommendation] += 1;
    }
    const coachEveryone = submitted
        .filter((s) => s.coachEveryone?.trim())
        .map((s) => ({ email: s.email, text: s.coachEveryone.trim() }));
    const gapConcern = submitted
        .filter((s) => s.gapConcern?.trim())
        .map((s) => ({ email: s.email, text: s.gapConcern.trim() }));
    return {
        candidateId,
        submittedCount: submitted.length,
        weightedOverall: mean(overalls),
        categoryAverages,
        categorySpread,
        signalAverages,
        recommendationTally,
        redFlagTally,
        coachEveryone,
        gapConcern,
    };
}
export { WEIGHT_TOTAL };
